import { PropsWithChildren } from 'react';
import { IconButton, makeStyles, Tooltip } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  useSidebarPinState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
// import { Administration } from '@backstage-community/plugin-rbac';
import { Administration } from '@internal/backstage-plugin-custom-rbac';
import { NotificationsSystem } from '@internal/backstage-plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const useRootContentStyles = makeStyles(theme => ({
  content: {
    minWidth: 0,
    width: '100%',
  },
  toolbar: {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.border}`,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  toggle: {
    color: theme.palette.navigation.color,
    borderRadius: 6,
    '&:hover': {
      background: theme.palette.navigation.navItem?.hoverBackground,
    },
  },
  toggleGlyph: {
    display: 'inline-block',
    fontSize: 24,
    lineHeight: 1,
    width: 24,
    textAlign: 'center',
  },
  navItem: {
    width: '100%',
  },
}));

const SidebarNavTooltip = ({
  label,
  children,
}: PropsWithChildren<{ label: string }>) => {
  const classes = useRootContentStyles();
  const { isOpen } = useSidebarOpenState();
  const { isMobile } = useSidebarPinState();

  return (
    <Tooltip title={!isMobile && !isOpen ? label : ''} placement="right" arrow>
      <div className={classes.navItem}>{children}</div>
    </Tooltip>
  );
};

const RootContent = ({ children }: PropsWithChildren<{}>) => {
  const classes = useRootContentStyles();
  const { isPinned, toggleSidebarPinState, isMobile } = useSidebarPinState();
  const label = isPinned ? 'Collapse sidebar' : 'Expand sidebar';

  return (
    <div className={classes.content}>
      {!isMobile && (
        <div className={classes.toolbar}>
          <Tooltip title={label}>
            <IconButton
              aria-label={label}
              aria-expanded={isPinned}
              className={classes.toggle}
              onClick={toggleSidebarPinState}
              size="small"
            >
              <span aria-hidden="true" className={classes.toggleGlyph}>
                {isPinned ? '‹' : '›'}
              </span>
            </IconButton>
          </Tooltip>
        </div>
      )}
      {children}
    </div>
  );
};

const CollapsedByDefaultSidebarPage = ({ children }: PropsWithChildren<{}>) => {
  // SidebarPage reads this preference on its first render and defaults to true.
  // Seed only a missing choice before it mounts; never overwrite a saved choice.
  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage.getItem('sidebarPinState') === null) {
        window.localStorage.setItem('sidebarPinState', 'false');
      }
    } catch {
      // The Backstage provider handles unavailable storage with its default.
    }
  }

  return <SidebarPage>{children}</SidebarPage>;
};

// NotificationsSystem provides useNotifications() and renders the notification
// bell. It wraps the whole layout, the sidebar included, so that every route
// can call notify() and Root stays the only child of AppRouter in App.tsx.
// convertLegacyAppRoot keeps that child as the app layout when the app moves
// to the New Frontend System (PRD #22 §6.2).
export const Root = ({ children }: PropsWithChildren<{}>) => (
  <NotificationsSystem>
    <CollapsedByDefaultSidebarPage>
      <Sidebar disableExpandOnHover>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarNavTooltip label="Search">
            <SidebarSearchModal />
          </SidebarNavTooltip>
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarNavTooltip label="Home">
            <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
          </SidebarNavTooltip>
          <SidebarNavTooltip label="My Groups">
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
          </SidebarNavTooltip>
          <SidebarNavTooltip label="APIs">
            <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          </SidebarNavTooltip>
          <SidebarNavTooltip label="Docs">
            <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          </SidebarNavTooltip>
          <SidebarNavTooltip label="Create...">
            <SidebarItem
              icon={CreateComponentIcon}
              to="create"
              text="Create..."
            />
          </SidebarNavTooltip>
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarNavTooltip label="RBAC">
          <Administration />
        </SidebarNavTooltip>
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarNavTooltip label="Settings">
            <SidebarSettings />
          </SidebarNavTooltip>
        </SidebarGroup>
      </Sidebar>
      <RootContent>{children}</RootContent>
    </CollapsedByDefaultSidebarPage>
  </NotificationsSystem>
);
